import React from 'react';
import { Box, Typography } from '@mui/material';
import { getMonthStart, getQuarterStart } from '../../utils';
import { DateOption, LocaleDateVariant } from '../../types';
import { useTheme } from '../../hooks';

/**
 * Returns a tick label for a date depending on the timespan that date options are grouped by (month, quarter, or year)
 * If the month/quarter label is January, then the year is also appended to the label
 */
export const getTimelineTickLabel = (variant: LocaleDateVariant, date: Date) => {
    switch (variant) {
        case LocaleDateVariant.shortMonthYear: {
            const monthStart = getMonthStart(date);
            const monthLabel = monthStart.toLocaleString(undefined, { month: 'short', timeZone: 'UTC' });
            return monthStart.getUTCMonth() === 0 ? `${monthLabel} ${monthStart.getUTCFullYear()}` : monthLabel;
        }
        case LocaleDateVariant.quarterly: {
            const quarterStart = getQuarterStart(date);
            const quarterLabel = quarterStart.toLocaleString(undefined, { month: 'short', timeZone: 'UTC' });
            return quarterStart.getUTCMonth() === 0 ? `${quarterLabel} ${quarterStart.getUTCFullYear()}` : quarterLabel;
        }
        case LocaleDateVariant.year:
            return String(date.getUTCFullYear());
    }
};

export type TimelineProps = {
    activeIndex: number;
    variant: LocaleDateVariant;
    globalDateOptions: DateOption[];
};

/**
 * Takes a list of time selector items and creates a timeline underneath the items with tick marks and labels.
 * Labels are based on the timespan variant passed in.
 */
export function Timeline({ activeIndex, variant, globalDateOptions }: TimelineProps) {
    const theme = useTheme();
    const { slideWidth, margin } = theme.timeSelector;
    const trackWidth = globalDateOptions.length * (slideWidth + margin * 2);
    const tickLabels = globalDateOptions.map((d) => getTimelineTickLabel(variant, new Date(d.value)));

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 50,
                width: `${trackWidth - margin}px`,
                height: 24,
                pointerEvents: 'none',
                marginLeft: `${margin}px`,
                marginTop: (theme) => theme.spacing(0.25)
            }}
        >
            {/* Active bar */}
            <Box
                data-testid="timeline-active-bar"
                sx={{
                    width: `${slideWidth}px`,
                    height: '4px',
                    position: 'absolute',
                    top: -4, // should match height above
                    left: activeIndex * (slideWidth + margin * 2),
                    backgroundColor: (theme) => theme.palette.primary.main,
                    borderTopLeftRadius: '2px',
                    borderTopRightRadius: '2px'
                }}
            />

            {/* Timeline line */}
            <Box
                sx={(theme) => ({
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '1px',
                    width: '100%',
                    backgroundColor: theme.palette.primary.main
                })}
            />

            {/* Ticks and labels */}
            {tickLabels.map((month, i) => (
                <Box
                    key={i}
                    data-testid="tick-label"
                    sx={{
                        position: 'absolute',
                        left: i * (slideWidth + margin * 2),
                        top: 0,
                        display: 'flex',
                        flexDirection: 'row',
                        height: 7
                    }}
                >
                    {/* Tick mark*/}
                    <Box
                        key={i}
                        sx={(theme) => ({
                            width: '1px',
                            height: 7,
                            backgroundColor: theme.palette.primary.main,
                            marginRight: theme.spacing(0.5)
                        })}
                    />
                    {/* Label */}
                    <Typography sx={{ fontSize: '0.65rem' }}>{month?.toLocaleUpperCase()}</Typography>
                </Box>
            ))}
        </Box>
    );
}
